import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual, omit } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import DeliverableTypeCounters from 'modules/deliverables/components/deliverable-type-counters/DeliverableTypeCounters';
import SubcampaignOfferActions from 'modules/subcampaigns/components/SubcampaignOfferActions';
import SubcampaignState from 'modules/subcampaigns/components/SubcampaignState';
import SubcampaignOfferDeclineModal from 'modules/subcampaigns/components/offer-decline-modal/SubcampaignOfferDeclineModal';
import { withModal } from 'components/modal/ModalHOC';
import Link from 'libs/router/Link';

import { renderPrice } from 'utils/helper/price';
import {
  getSubcampaignDisplayBudget,
  isAccepted,
  isInfluencerActionRequired,
  isNegotiationsReopened,
  isQuote,
  isQuoteProvided
} from 'utils/subcampaign';
import { __t, applyTranslations } from 'utils/language';

import { DELIVERABLE_TYPE_ZALANDO_OUTFIT } from 'utils/deliverables';

import type { Campaign, Deliverable, Subcampaign } from 'types';

applyTranslations(require('./messenger-subcampaign-nav.i18n.json'));

const DeclineLinkWithModal = withModal(<span />, {
  modalContent: SubcampaignOfferDeclineModal
});

type StateProps = {
  subcampaign: Subcampaign,
  deliverables: Array<Deliverable>,
  variablePaymentsAmount: number
};

type OwnProps = {
  campaign?: Campaign
};

type Props = StateProps & OwnProps;

export class MessengerSubcampaignNav extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    const {
      subcampaign,
      campaign
    } = this.props;

    /*
      Omitting the `isLoading` flag, because it causes unnecessary re-renders.
      The current context where we find this is unnecessary is clicking the `Decline` button to decline an offer.
      Due to this, the modal to decline the offer disappears after 2 seconds.
    */
    const omittedSubcampaignProps = omit(subcampaign, 'isLoading');
    const omittedSubcampaignNextProps = omit(nextProps.subcampaign, 'isLoading');

    return !isEqual(omittedSubcampaignProps, omittedSubcampaignNextProps) ||
      !isEqual(campaign, nextProps.campaign);
  }

  renderOfferPreview = (subcampaign: Subcampaign) => {
    const originalOrUpdatedSubcampaign = {
      ...subcampaign,
      offer: subcampaign.updatedOffer || subcampaign.offer
    };

    return (
      <div className="messenger-subcampaign-nav__offer-preview">
        <span>{__t('messenger_subcampaign_nav.offer')}</span>
        <div className="messenger-subcampaign-nav__state">
          <SubcampaignState
            subcampaign={subcampaign}
          />
        </div>
        <div className="messenger-subcampaign-nav__budget">
          {getSubcampaignDisplayBudget(originalOrUpdatedSubcampaign)}
        </div>
      </div>
    );
  };

  renderVariablePayments = (subcampaign: Subcampaign, variablePaymentsAmount: number) => {
    if (!variablePaymentsAmount) {
      return null;
    }

    const campaign = subcampaign.campaign;

    return (
      <div className="messenger-subcampaign-nav__variable-payments">
        <ActionLink
          size="inherit"
          color="light"
          link={`/campaigns/${campaign.id}/details/${subcampaign.id}/payments`}
          openInNewTab
        >
          {__t('messenger_subcampaign_nav.variable_payment')}
        </ActionLink>
        <div className="messenger-subcampaign-nav__budget">
          {renderPrice(variablePaymentsAmount)}
        </div>
      </div>
    );
  };

  renderTasks(): React.ReactNode {
    const { deliverables } = this.props;

    if (!deliverables.length) {
      return null;
    }

    return (
      <div className="messenger-subcampaign-nav__tasks">
        <div>
          <span className="messenger-subcampaign-nav__label">
            {__t('messenger_subcampaign_nav.deliverables_label')}
          </span>
          <span className="messenger-subcampaign-nav__value">
            {(deliverables || []).length}
          </span>
        </div>
        <div className="messenger-subcampaign-nav__row">
          <DeliverableTypeCounters deliverables={deliverables} />
        </div>
      </div>
    );
  }

  renderQuote(subcampaign: Subcampaign): React.ReactNode {
    const isQuoteProvidedByCreator = isQuoteProvided(subcampaign);
    return (
      <React.Fragment>
        <div className="messenger-subcampaign-nav__offer-title">
          {isQuoteProvidedByCreator ? __t('messenger_subcampaign_nav.quote_provided') : __t('messenger_subcampaign_nav.quote_requested')}
        </div>
        {
          isQuoteProvidedByCreator && (
            <React.Fragment>
              <div className="messenger-subcampaign-nav__offer-sub-title">
                {__t('messenger_subcampaign_nav.quote_provided_label', { offer: subcampaign.offer.quote })}
              </div>
              <Link
                target="_blank"
                to={`/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}`}
                className="messenger-subcampaign-nav__message-link"
              >
                {__t('messenger_subcampaign_nav.quote_link_view_brief')}
              </Link>
            </React.Fragment>
          )
        }
        {
          !isQuoteProvidedByCreator && (
            <React.Fragment>
              <div className="messenger-subcampaign-nav__message">
                {`${__t('messenger_subcampaign_nav.quote_requested_message_part1')} `}
                <Link
                  target="_blank"
                  to={`/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}`}
                  className="messenger-subcampaign-nav__message-link"
                >
                  {__t('messenger_subcampaign_nav.quote_link_to_brief')}
                </Link>
                {` ${__t('messenger_subcampaign_nav.quote_requested_message_part2')}`}
              </div>
              <div className="messenger-subcampaign-nav__message">
                {__t('messenger_subcampaign_nav.quote_requested_message_part3')}
                <DeclineLinkWithModal
                  className="messenger-subcampaign-nav__message-link"
                  modalContentProps={{
                    subcampaign
                  }}
                >
                  {__t('messenger_subcampaign_nav.quote_button_decline')}
                </DeclineLinkWithModal>
              </div>
            </React.Fragment>
          )
        }
      </React.Fragment>
    );
  }

  render() {
    const { subcampaign, variablePaymentsAmount, deliverables } = this.props;

    if (!subcampaign || !subcampaign.id) {
      return null;
    }

    const containsContentApprovalTask = deliverables.some(deliverable => deliverable.contentApprovalTimeline.status);
    const oneNonOutfitTask = deliverables.some(d => d.deliverableType !== DELIVERABLE_TYPE_ZALANDO_OUTFIT);

    const campaign = subcampaign.campaign;

    return (
      <section className="messenger-subcampaign-nav">
        <div className="messenger-subcampaign-nav__box">
          <div className="messenger-subcampaign-nav__inner">
            {this.renderOfferPreview(subcampaign)}
            {this.renderVariablePayments(subcampaign, variablePaymentsAmount)}
          </div>
        </div>
        <div className="messenger-subcampaign-nav__content">
          {this.renderTasks()}
          {
            isAccepted(subcampaign) && (
              <div className="messenger-subcampaign-nav__content-buttons-container">
                {
                  containsContentApprovalTask && (
                    <ActionButton
                      link={
                        `/campaigns/${campaign.id}/details/${subcampaign.id}?view=content_approval#deliverables`
                      }
                    >
                      {__t('messenger_subcampaign_nav.subcampaign_posts_add')}
                    </ActionButton>
                  )
                }
                {
                  oneNonOutfitTask && (
                    <ActionButton
                      className="messenger-subcampaign-nav__insights-button"
                      link={`/campaigns/${campaign.id}/details/${subcampaign.id}?view=insights#deliverables`}
                    >
                      {__t('messenger_subcampaign_nav.subcampaign_insights_add')}
                    </ActionButton>
                  )
                }
              </div>
            )
          }
        </div>
        {
          isInfluencerActionRequired(subcampaign, subcampaign.campaign) && (
            <div className="messenger-subcampaign-nav__content">
              {
                isQuote(subcampaign)
                  ? this.renderQuote(subcampaign)
                  : (
                    <React.Fragment>
                      <SubcampaignOfferActions
                        buttonWidth="maximum"
                        subcampaign={subcampaign}
                      />
                      {
                        isNegotiationsReopened(subcampaign) && (
                          <div className="messenger-subcampaign-nav__disclaimer">
                            {__t('messenger_subcampaign_nav.renegotiation_disclaimer')}
                          </div>
                        )
                      }
                    </React.Fragment>
                  )
              }
            </div>
          )
        }
      </section>
    );
  }
}


const mapStateToProps = ({ subcampaign, offerDeliverables, subcampaignVariablePayments }): StateProps => ({
  subcampaign: subcampaign.data,
  variablePaymentsAmount: (subcampaignVariablePayments.variablePayments || {}).totalAmount || 0,
  deliverables: (subcampaign.data && subcampaign.data.offer && offerDeliverables.deliverablesByOfferId[subcampaign.data.offer.id]) || []
});

export default connect(mapStateToProps)(MessengerSubcampaignNav);
